import React, { useState, useEffect } from 'react';
import {
    CircularProgress,
    Backdrop,
    Typography,
    useTheme,
    Grid,
    Button,
    Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import doRequest from '../../lib/services/apiRequestor';
import RowLevelFilterDialog from './RowLevelFilterDialog';
import DeleteConfirmationDialog from '../../lib/components/DeleteConfirmationDialog';
import RequestStatusAlert from '../../lib/components/RequestStatusAlert';

export default function RowLevelFiltersTab({ value, index, idToken, drawerWidth }) {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState([]);
    const [statusMessage, setStatusMessage] = useState(null);
    const [severity, setSeverity] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState(null);

    useEffect(() => {
        if (value === index && idToken) {
            loadFilters();
        }
    }, [value, index, idToken]);

    const loadFilters = () => {
        setIsLoading(true);
        doRequest('/api/RowLevelFilters', idToken)
            .then((response) => {
                if (!response.isError) {
                    setFilters(response.data || []);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                setStatusMessage('Failed to load filters');
                setSeverity('error');
            });
    };

    const handleNewFilter = () => {
        setSelectedFilter(null);
        setOpenDialog(true);
    };

    const handleEditClick = (id) => async () => {
        setIsLoading(true);
        const response = await doRequest(`/api/RowLevelFilters/${id}`, idToken);
        if (!response.isError) {
            setSelectedFilter(response.data);
            setOpenDialog(true);
        } else {
            setStatusMessage('Failed to load filter details');
            setSeverity('error');
        }
        setIsLoading(false);
    };

    const handleDeleteClick = (id) => () => {
        const filter = filters.find((f) => f.id === id);
        setSelectedFilter(filter);
        setOpenDelete(true);
    };

    const handleDeleteConfirm = () => {
        if (!selectedFilter) return;

        setIsLoading(true);
        doRequest(`/api/RowLevelFilters/${selectedFilter.id}`, idToken, {
            method: 'DELETE'
        })
            .then((response) => {
                if (!response.isError) {
                    setStatusMessage('Filter deleted successfully');
                    setSeverity('success');
                    loadFilters();
                } else {
                    setStatusMessage('Failed to delete filter');
                    setSeverity('error');
                }
                setOpenDelete(false);
                setIsLoading(false);
            })
            .catch(() => {
                setStatusMessage('Error deleting filter');
                setSeverity('error');
                setOpenDelete(false);
                setIsLoading(false);
            });
    };

    const handleDialogClose = (refresh = false) => {
        setOpenDialog(false);
        setSelectedFilter(null);
        if (refresh) {
            loadFilters();
            setStatusMessage('Filter saved successfully');
            setSeverity('success');
        }
    };

    return (
        <div hidden={value !== index}>
            <Backdrop sx={{ color: '#055A60', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Grid container spacing={2} style={{ width: 'auto', marginTop: '2px', marginLeft: drawerWidth - 15 }}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Row Level Filters</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleNewFilter}
                        >
                            New Filter
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            height: 500,
                            width: '100%',
                            '& .super-app-theme--header': {
                                backgroundColor: '#A5D7DA',
                                color: theme.palette.common.white,
                            },
                            '& .MuiDataGrid-cell:hover': {
                                color: '#377B7F',
                                cursor: 'pointer'
                            },
                        }}
                    >
                        <DataGrid
                            rows={filters}
                            columns={[
                                {
                                    field: 'name',
                                    headerClassName: 'super-app-theme--header',
                                    headerAlign: 'left',
                                    headerName: 'Name',
                                    flex: 1,
                                    sortable: false,
                                },
                                {
                                    field: 'description',
                                    headerClassName: 'super-app-theme--header',
                                    headerAlign: 'left',
                                    headerName: 'Description',
                                    flex: 1,
                                    sortable: false,
                                },
                                {
                                    field: 'filterType',
                                    headerClassName: 'super-app-theme--header',
                                    headerAlign: 'left',
                                    headerName: 'Type',
                                    flex: 0.5,
                                    sortable: false,
                                },
                                {
                                    field: 'itemCount',
                                    headerClassName: 'super-app-theme--header',
                                    headerAlign: 'center',
                                    headerName: 'Items',
                                    flex: 0.3,
                                    sortable: false,
                                },
                                {
                                    field: 'actions',
                                    type: 'actions',
                                    headerName: 'Actions',
                                    headerClassName: 'super-app-theme--header',
                                    headerAlign: 'center',
                                    flex: 0.4,
                                    sortable: false,
                                    getActions: ({ id }) => [
                                        <GridActionsCellItem
                                            icon={<EditIcon />}
                                            label="Edit"
                                            sx={{ color: 'primary.main', marginLeft: '5px' }}
                                            onClick={handleEditClick(id)}
                                        />,
                                        <GridActionsCellItem
                                            icon={<DeleteIcon />}
                                            label="Delete"
                                            sx={{ color: 'error.main', marginLeft: '5px' }}
                                            onClick={handleDeleteClick(id)}
                                        />,
                                    ],
                                },
                            ]}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                    printOptions: { disableToolbarButton: true }
                                },
                            }}
                        />
                    </Box>
                </Grid>
                <RowLevelFilterDialog
                    idToken={idToken}
                    open={openDialog}
                    onClose={handleDialogClose}
                    filter={selectedFilter}
                />
                <DeleteConfirmationDialog
                    idToken={idToken}
                    open={openDelete}
                    setOpen={setOpenDelete}
                    page="RowLevelFilters"
                    message={`Are you sure you want to delete "${selectedFilter?.name}"?`}
                    data={selectedFilter}
                    onConfirm={handleDeleteConfirm}
                    updateStausMessage={() => {}}
                />
                <Grid item xs={12}>
                    <RequestStatusAlert message={statusMessage} severity={severity} />
                </Grid>
            </Grid>
        </div>
    );
}
